import { environment } from '../../../../environments/environment';
import { CKEditorConfig } from './CKEditorConfig';
import { QualioSectionHeadingPlugin } from '../plugins/SectionHeaders/QualioSectionHeadingPlugin';
import {
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Editor,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  HorizontalLine,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageTextAlternative,
  ImageStyle,
  ImageUpload,
  Indent,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  TextTransformation,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  ImageToolbar,
  LinkImage,
  Link,
  MediaEmbed,
  IndentBlock,
  Italic,
  List,
  ListProperties,
  Strikethrough,
  Subscript,
  Superscript,
  Heading,
  CodeBlock,
  Underline,
  Table,
  TableColumnResize,
  FileRepository,
  TableProperties,
  TableCellProperties,
  TableCaption,
  TableToolbar,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Plugin,
  RemoveFormat,
} from 'ckeditor5';
import { MultiLevelList } from 'ckeditor5-premium-features';

declare global {
  const CKEditor5: any;
  const FileUpload: any;
  const SectionList: any;
  const Smartlink: any;
  const Checkbox: any;
}

export class EditorConfig {
  public static readonly CONTENT_EDITOR_PLUGINS: typeof Plugin[] = [
    Alignment,
    Autoformat,
    AutoImage,
    AutoLink,
    Autosave,
    FileUpload.QualioFileUpload,
    BlockQuote,
    HorizontalLine,
    Essentials,
    Paragraph,
    Bold,
    FontColor,
    FontSize,
    FontFamily,
    FontBackgroundColor,
    Image,
    ImageInsert,
    ImageUpload,
    ImageCaption,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    PageBreak,
    ImageToolbar,
    LinkImage,
    MediaEmbed,
    Indent,
    IndentBlock,
    RemoveFormat,
    Italic,
    Link,
    List,
    ListProperties,
    SectionList.QualioSectionList,
    QualioSectionHeadingPlugin,
    MultiLevelList,
    PasteFromOffice,
    Mention,
    Checkbox.QualioCheckbox,
    Smartlink.QualioSmartlink,
    FileRepository,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    SpecialCharactersEssentials,
    Strikethrough,
    Superscript,
    Subscript,
    Heading,
    CodeBlock,
    Underline,
    Table,
    TableColumnResize,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    TableCaption,
    TextTransformation,
  ];

  public static readonly CONTENT_EDITOR_TOOLBAR_ITEMS: string[] = [
    'undo',
    'redo',
    '|',
    'heading',
    '|',
    'bold',
    'italic',
    'underline',
    'fontSize',
    'fontColor',
    '|',
    'link',
    'qualioFileUploadButton',
    'uploadImage',
    '|',
    'alignment',
    '|',
    'bulletedList',
    'numberedList',
    'qualioSectionListButton',
    'multiLevelList',
    '|',
    'outdent',
    'indent',
    '|',
    'insertTable',
    'qualioCheckboxButton',
    'specialCharacters',
    'subscript',
    'superscript',
    '|',
    'codeBlock',
    'blockQuote',
    '|',
    'fontBackgroundColor',
    'strikethrough',
    '|',
    'removeFormat',
    'pageBreak',
  ];

  public static readonly TABLE_TOOLBAR_ITEMS: string[] = [
    'tableColumn',
    'tableRow',
    'mergeTableCells',
    'toggleTableCaption',
    'tableProperties',
    'tableCellProperties',
  ];

  public static readonly IMAGE_TOOLBAR_ITEMS: string[] = [
    'toggleImageCaption',
    'imageTextAlternative',
    '|',
    'imageStyle:alignLeft',
    'imageStyle:alignRight',
    'imageStyle:alignBlockLeft',
    'imageStyle:alignBlockRight',
    'imageStyle:alignCenter',
  ];

  public static readonly DocumentEditorSmartlinkSuggestTimeout = 100;
}

export const getCKEditorConfig = (
  companyId: number,
  autosaveHandler?: (editor: Editor) => Promise<void>,
): CKEditorConfig => {
  const contentEditorPlugins = [...EditorConfig.CONTENT_EDITOR_PLUGINS];
  return {
    plugins: contentEditorPlugins,
    toolbar: {
      items: EditorConfig.CONTENT_EDITOR_TOOLBAR_ITEMS,
      shouldNotGroupWhenFull: false,
    },
    licenseKey: process.env.REACT_APP_CKE_PREMIUM_FEATURES_LICENSE_KEY,
    indentBlock: {
      offset: 2,
      unit: 'em',
    },
    table: {
      contentToolbar: EditorConfig.TABLE_TOOLBAR_ITEMS,
    },
    image: {
      toolbar: EditorConfig.IMAGE_TOOLBAR_ITEMS,
    },
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_p',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_h1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_h2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_h3',
        },
        {
          model: 'heading4',
          view: 'h4',
          title: 'Heading 4',
          class: 'ck-heading_h4',
        },
      ],
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
    },
    mention: Smartlink.QualioSmartlink.getMentionConfig(
      companyId,
      environment.GLOBAL_SEARCH_SERVICE_URL,
    ),
    fontFamily: {
      options: ['default'],
    },
    fontSize: {
      options: [
        'small',
        {
          title: 'Normal',
          model: '14px',
          upcastAlso: [],
          view: '',
        },
        {
          title: 'Large',
          model: '19.6px',
          upcastAlso: [],
          view: '',
        },
      ],
    },
    simpleFileUpload: {
      url: environment.MEDTECH_API + '/attachments',
      fileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.png'],
    },
    simpleUpload: {
      uploadUrl: environment.MEDTECH_API + '/attachments',
      withCredentials: true,
    },
    autosave: {
      save: autosaveHandler,
      waitingTime: 3000,
    },
  };
};
